/**
 * 이상한 방 - 계정 단위 탄창
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 4.3절(탄환 경제) · `이상한방-상태모델-v0.1.md` 11.3절
 *
 * ## 왜 방에 떠 있던 아이템을 걷어냈는가
 *
 * 직전까지 탄환 보급은 **방 바닥에 놓인 월드 아이템**이었다. 주우러 가는 동선이 곧 관찰 시간이
 * 된다는 계산이었지만, 그 설계는 두 가지를 동시에 망가뜨렸다.
 *
 *  1. **라운드 안의 오브젝트가 하나 늘었다.** 방에 있는 물건이 '변화'가 아니면서 화면에 있으면
 *     플레이어는 그것도 일단 의심한다. 관찰 게임에서 의심할 대상을 규칙 밖에서 늘리는 것은
 *     난이도가 아니라 잡음이다.
 *  2. **보급이 라운드에 갇혔다.** 광고·결제·미니게임으로 보상을 주려면 그 보상이 라운드 밖에
 *     존재해야 하는데, 월드 아이템은 라운드가 끝나면 사라진다.
 *
 * 그래서 탄창은 **계정 단위 메타 자원**이다. 방이 아니라 사람이 들고 있고, 라운드를 넘어 살아남고,
 * HUD 버튼 하나로 쓴다. 저장은 `progress.ts`가 맡는다(같은 localStorage 레코드에 얹힌다).
 *
 * ## 지켜야 하는 밸런스 원칙
 *
 * **탄창 없이도 모든 단계를 클리어할 수 있어야 한다.** 탄창이 없으면 진행이 막히는 순간
 * 그건 페이월이고, 튜토리얼에서 막히면 멀티까지 도달을 못 한다. 그 불변식은 이 파일이 아니라
 * `levels.ts`가 데이터로 지키고 `levels.test.ts`가 고정한다 - 여기서는 그 사실만 못박아 둔다.
 * 탄창은 **실수를 만회하는 보조**이지 진행의 재료가 아니다.
 *
 * ## 이 파일이 시계도 난수도 읽지 않는 이유
 *
 * `rng.ts` 상단의 규칙을 그대로 따른다. 그래서 획득 이력의 시각도 `Date.now()`가 아니라
 * **단조 증가하는 일련번호**(`seq`)다. 벽시계는 어차피 클라이언트에서 믿을 수 없고
 * (기기 시각을 되돌리면 그만이다), 복구에 필요한 것은 "언제"가 아니라 "어떤 순서로"다.
 */

/* --- 타입 ------------------------------------------------------------------- */

/**
 * 탄창이 들어온 경로.
 *
 * **지금 실제로 동작하는 것은 `debug`와 `envelope` 둘이다.** 봉투(`economy/envelope.ts`)가 첫 실제
 * 지급 경로다 - 핀으로 봉투를 열면 탄창이 나온다. 나머지는 자리만 열어 둔 것이며
 * (광고 SDK·결제·미니게임은 각자 별도의 작업이다), 붙기 전에 호출하면 `grantMagazines`가 던진다.
 * 조용히 성공시키지 않는 이유는 하나다: 지급되지 않았는데 성공으로 읽히면 그 순간
 * "결제했는데 안 들어왔다"가 되고, 이력만으로는 원인을 가릴 수 없게 된다.
 *
 * `purchase`는 **미구현 그대로 둔다**(로드맵 3.6절). 결제가 붙는 날 그 등록이 첫 줄이고,
 * 그때도 살 수 있는 것은 핀뿐이다 - 탄창을 직접 파는 경로는 열지 않는다.
 */
export type MagazineSource = 'ad' | 'purchase' | 'minigame' | 'daily' | 'envelope' | 'debug'

/** 유효한 경로 전량. 저장값 검증(`parseMagazineState`)이 이 목록으로 낯선 문자열을 걸러 낸다. */
export const MAGAZINE_SOURCES: readonly MagazineSource[] = [
  'ad',
  'purchase',
  'minigame',
  'daily',
  'envelope',
  'debug',
]

/** 지금 실제로 지급 경로가 구현된 것. 여기 없는 source는 `grantMagazines`가 거부한다. */
const IMPLEMENTED_SOURCES: ReadonlySet<MagazineSource> = new Set<MagazineSource>(['envelope', 'debug'])

/**
 * 지급 이력 한 줄.
 *
 * **이력을 남기기로 한 근거**는 앞으로 붙을 광고·결제다. 붙는 순간 "봤는데 안 들어왔다" ·
 * "샀는데 안 들어왔다"는 문의가 반드시 오고, 그때 클라이언트에 아무 흔적이 없으면 답할 수 있는
 * 말이 "다시 해 보세요"밖에 없다. 이 로그는 증빙이 아니라(로컬 값이므로 위조 가능하다)
 * **복구의 출발점**이다 - 어떤 경로로 몇 개가 언제쯤(순서상) 들어왔는지가 남으면
 * 서버 영수증과 대조할 수 있다.
 *
 * `requested`와 `granted`를 나눠 두는 것이 핵심이다. 둘이 다른 유일한 경우가
 * **상한에 걸려 일부만 들어간 지급**이고, 그게 정확히 사람이 손해를 본 순간이다.
 */
export type MagazineGrant = Readonly<{
  /** 1부터 시작하는 단조 증가 번호. 시계를 읽지 않으므로 순서는 이 값이 유일한 근거다. */
  seq: number
  source: MagazineSource
  /** 지급하려 한 양. */
  requested: number
  /** 실제로 들어간 양. 상한에 걸리면 `requested`보다 작고, 0일 수도 있다. */
  granted: number
  /** 지급 직후 보유량. 로그가 잘려 나가도 이 값으로 중간 소비량을 역산할 수 있다. */
  balanceAfter: number
}>

/** 계정이 들고 있는 탄창 전체. */
export type MagazineState = Readonly<{
  count: number
  grantLog: readonly MagazineGrant[]
}>

/** 소비 결과. `ok`가 거짓이면 상태는 **입력과 같은 참조**다(참조 비교로 "변화 없음"을 알 수 있다). */
export type ConsumeResult = Readonly<{ state: MagazineState; ok: boolean }>

/* --- 상수 ------------------------------------------------------------------- */

/**
 * 보유 상한.
 *
 * **상한을 두기로 한 근거**는 결제 밸런싱이다. 무한 누적이면 "탄창 10개 묶음"의 가치가
 * 보유량에 따라 제멋대로 달라지고(0개인 사람에게는 구원, 300개인 사람에게는 무의미),
 * 그 상태로는 가격을 정할 근거 자체가 없다. 상한이 있으면 "가득 찬 사람에게는 팔지 않는다"는
 * 규칙을 세울 수 있고, 그게 정직한 상점의 최소 조건이다.
 *
 * 값이 99인 것은 두 가지 타협이다. 낮게 잡으면(예: 20) 10개 묶음을 산 사람이 상한에 걸려
 * 손해를 보는 일이 실제로 생기고, 그건 환불 사유다. 높게 잡으면 상한이 있으나 마나다.
 * 두 자리는 HUD 배지가 잘리지 않는 폭이기도 하다.
 */
export const MAX_MAGAZINE_COUNT = 99

/**
 * 처음 시작하는 사람에게 주는 양.
 *
 * 3개인 이유가 셋이다.
 *  - **0이면 기능이 없는 것과 같다.** 버튼이 회색으로만 존재하면 그게 무엇인지 배울 기회가 없고,
 *    나중에 상점이 열렸을 때 처음 보는 물건을 사라는 말이 된다.
 *  - **떨어져야 한다.** 무한히 있으면 라운드의 잔탄 긴장이 통째로 사라진다. 3개는 초반 몇 판에
 *    다 쓰이고, 그 뒤에 사람은 "없어도 깰 수 있다"를 직접 확인하게 된다 - 그 확인이
 *    나중의 광고·결제를 강요가 아니라 선택으로 만든다.
 *  - **라운드당 상한(`tuning.maxMagazinesPerRound`)이 2**라서, 3개는 "한 판을 최대로 구제하고
 *    한 번 더 여유가 있는" 양이다.
 */
export const INITIAL_MAGAZINE_COUNT = 3

/**
 * 남겨 두는 지급 이력 수.
 *
 * 무한히 쌓지 않는 이유는 저장 용량이다. `localStorage`는 용량을 넘기면 `setItem`이 던지고,
 * 그 순간 **진행 기록까지 통째로 저장되지 않는다**(`progress.saveProgress`는 한 덩어리를 쓴다).
 * 탄창 이력 때문에 클리어 기록이 날아가는 것은 어떤 복구 이득으로도 정당화되지 않는다.
 *
 * 20개면 최근 몇 주의 지급을 덮는다. 그보다 오래된 문의는 어차피 서버 영수증으로 가야 한다.
 */
export const MAGAZINE_LOG_LIMIT = 20

/* --- 생성 ------------------------------------------------------------------- */

/**
 * 새 계정의 탄창 상태.
 *
 * 초기 지급을 **로그에 남기지 않는다.** 로그는 "받은 것"의 이력인데, 이것은 준 적이 없는
 * 시작값이기 때문이다. 남기면 첫 줄이 언제나 `debug`도 `purchase`도 아닌 유령 항목이 되고,
 * 그 유령을 예외 처리하는 코드가 이력을 읽는 모든 곳에 생긴다.
 */
export function createMagazineState(count: number = INITIAL_MAGAZINE_COUNT): MagazineState {
  return { count: clampCount(count), grantLog: [] }
}

/** 상한과 0 사이로 자른다. 소수·NaN은 0으로 떨어진다 - 저장값이 오염된 경로가 여기로 모인다. */
function clampCount(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 0
  return Math.min(MAX_MAGAZINE_COUNT, Math.floor(value))
}

/* --- 셀렉터 ------------------------------------------------------------------ */

/** 이 경로가 실제로 동작하는가. UI는 이 값으로 아직 없는 버튼을 아예 그리지 않는다. */
export function isMagazineSourceImplemented(source: MagazineSource): boolean {
  return IMPLEMENTED_SOURCES.has(source)
}

/** 지금 한 개를 쓸 수 있는가. 라운드 쪽 조건(페이즈·잔탄)은 `gameState.canUseMagazine`이 본다. */
export function canConsumeMagazine(state: MagazineState): boolean {
  return state.count > 0
}

/** 다음 이력 번호. 로그가 잘려도 마지막 줄에서 이어지므로 번호는 되돌아가지 않는다. */
function nextSeq(state: MagazineState): number {
  const last = state.grantLog[state.grantLog.length - 1]
  return (last?.seq ?? 0) + 1
}

/* --- 지급 ------------------------------------------------------------------- */

/**
 * 탄창을 지급한다.
 *
 * **미구현 경로는 던진다.** 조용히 무시하거나 성공으로 돌려주지 않는 이유:
 * 이 함수를 부르는 것은 사람이 아니라 우리가 쓸 코드이고, `purchase`를 부를 수 있다는 것은
 * 결제 연동을 붙였다는 뜻이다. 그 시점에 지급이 no-op이면 **돈은 받고 물건은 안 준 상태**가
 * 조용히 배포된다. 던지면 늦어도 첫 테스트에서 걸린다.
 *
 * 상한에 걸린 지급은 던지지 않고 **잘라서 넣고 이력에 남긴다.** 구매 흐름 한가운데서 예외를
 * 던지면 결제는 이미 끝났는데 앱이 죽는 최악의 순서가 되기 때문이다.
 *
 * @throws {RangeError} `count`가 1 이상 `MAX_MAGAZINE_COUNT` 이하의 정수가 아닐 때
 * @throws {Error} 아직 구현되지 않은 `source`일 때
 */
export function grantMagazines(
  state: MagazineState,
  count: number,
  source: MagazineSource,
): MagazineState {
  if (!Number.isSafeInteger(count) || count < 1 || count > MAX_MAGAZINE_COUNT) {
    throw new RangeError(
      `grantMagazines: 지급량은 1~${String(MAX_MAGAZINE_COUNT)}의 정수여야 한다 (받은 값: ${String(count)})`,
    )
  }
  if (!isMagazineSourceImplemented(source)) {
    throw new Error(
      `grantMagazines: '${source}' 경로는 아직 붙지 않았다. ` +
        '자리만 열려 있는 상태에서 지급을 성공으로 처리하면 "받았는데 안 들어왔다"가 된다',
    )
  }

  const balanceAfter = clampCount(state.count + count)
  const granted = balanceAfter - state.count
  const grant: MagazineGrant = { seq: nextSeq(state), source, requested: count, granted, balanceAfter }

  return { count: balanceAfter, grantLog: trimLog([...state.grantLog, grant]) }
}

/** 오래된 이력부터 버린다. 최근 것이 문의에 쓰이는 것이므로 뒤를 남긴다. */
function trimLog(log: readonly MagazineGrant[]): readonly MagazineGrant[] {
  return log.length <= MAGAZINE_LOG_LIMIT ? log : log.slice(log.length - MAGAZINE_LOG_LIMIT)
}

/* --- 소비 ------------------------------------------------------------------- */

/**
 * 한 개를 쓴다. 없으면 `ok: false`이고 상태는 그대로다.
 *
 * **소비는 이력에 남기지 않는다.** 이력의 목적이 "받은 것"의 복구라서다. 소비는 라운드마다
 * 일어나 금방 로그를 채우고, 그러면 정작 지급 한 줄이 20줄 상한 밖으로 밀려난다.
 * 지급 줄의 `balanceAfter`와 현재 `count`의 차이가 곧 그 사이의 소비량이므로,
 * 복구에 필요한 정보는 남기지 않아도 남아 있다.
 */
export function consumeMagazine(state: MagazineState): ConsumeResult {
  if (!canConsumeMagazine(state)) return { state, ok: false }
  return { state: { ...state, count: state.count - 1 }, ok: true }
}

/* --- 저장값 읽기 -------------------------------------------------------------- */

function isRecordLike(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isMagazineSource(value: unknown): value is MagazineSource {
  return typeof value === 'string' && (MAGAZINE_SOURCES as readonly string[]).includes(value)
}

function parseGrant(value: unknown): MagazineGrant | null {
  if (!isRecordLike(value)) return null

  const seq = value['seq']
  const source = value['source']
  if (typeof seq !== 'number' || !Number.isSafeInteger(seq) || seq < 1) return null
  if (!isMagazineSource(source)) return null

  return {
    seq,
    source,
    requested: clampCount(Number(value['requested'])),
    granted: clampCount(Number(value['granted'])),
    balanceAfter: clampCount(Number(value['balanceAfter'])),
  }
}

/**
 * 저장값 → 탄창 상태. **어떤 입력에도 던지지 않는다.**
 *
 * 읽을 수 없으면 **초기 지급 상태**로 떨어진다. 0으로 떨어뜨리지 않는 이유는 손해의 방향이다 -
 * 저장이 깨지는 것은 사람의 잘못이 아닌데(브라우저 정리·용량 초과·포맷 변경) 그때마다 산 물건을
 * 빼앗으면 그건 버그가 아니라 사고다. 반대 방향의 악용(저장을 지워 3개를 다시 받기)은
 * 지금 구조에서 **어떤 판단을 해도 막을 수 없다** - 계정이 로컬 저장 하나뿐이라 통째로 지우면
 * 새 사람이 되기 때문이다. 서버가 생기면 진실은 서버가 쥐고 이 함수는 캐시 파서가 된다.
 */
export function parseMagazineState(value: unknown): MagazineState {
  if (!isRecordLike(value)) return createMagazineState()

  const rawCount = value['count']
  if (typeof rawCount !== 'number') return createMagazineState()

  const rawLog = value['grantLog']
  const grantLog: MagazineGrant[] = []
  if (Array.isArray(rawLog)) {
    for (const entry of rawLog) {
      const grant = parseGrant(entry)
      if (grant !== null) grantLog.push(grant)
    }
  }

  return { count: clampCount(rawCount), grantLog: trimLog(grantLog) }
}
